import React, { useContext, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logos, uiIcons } from '@/assets/piecrip/images';
import { ThemeContext } from '@/theme/ThemeProvider';
import { useChangeLanguage } from '@app/organisms/Header/BtnLanguageChange/hooks';
import * as S from './styles';
import { useToken } from '@/app/hooks';

interface PiecripHeaderProps {
  transparent?: boolean;
  activeMenu?: string;
  hasNotification?: boolean;
}

const PiecripHeader: React.FC<PiecripHeaderProps> = ({
  transparent = false,
  activeMenu = 'Trade',
  hasNotification = false,
}) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { isDarkMode, toggleTheme } = useContext(ThemeContext);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const languageButtonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [currentLang, setCurrentLang] = useState(i18n.language);
  const [searchQuery, setSearchQuery] = useState('');

  const { data: token } = useToken();
  const isLoggedIn = !!token;

  useChangeLanguage(currentLang);

  // i18n 언어 변경 감지
  useEffect(() => {
    setCurrentLang(i18n.language);
  }, [i18n.language]);

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleThemeToggle = () => {
    toggleTheme();
  };

  const handleLanguageToggle = () => {
    setIsLanguageDropdownOpen((prev) => !prev);
  };

  const handleLanguageChange = (lang: string) => {
    setCurrentLang(lang);
    setIsLanguageDropdownOpen(false);
  };

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        languageButtonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !languageButtonRef.current.contains(event.target as Node)
      ) {
        setIsLanguageDropdownOpen(false);
      }
    };

    if (isLanguageDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLanguageDropdownOpen]);

  const languages = [
    { code: 'ko', name: '한국어' },
    { code: 'en', name: 'English' },
  ];

  const menuItems = [
    { label: t('piecrip.header.menu.trade'), key: 'Trade', path: '/' },
    {
      label: t('piecrip.header.menu.markets'),
      key: 'Markets',
      path: '/markets',
    },
    { label: t('piecrip.header.menu.earn'), key: 'Earn', path: '/earn' },
    {
      label: t('piecrip.header.menu.academy'),
      key: 'Academy',
      path: '/academy',
    },
    {
      label: t('piecrip.header.menu.community'),
      key: 'Community',
      path: '/community',
    },
    {
      label: t('piecrip.header.menu.support'),
      key: 'Support',
      path: '/support',
    },
  ];

  // 로고 이미지 선택 (다크모드일 때 piecripDark, 아니면 piecrip)
  const logoSrc = isDarkMode ? logos.piecripDark : logos.piecrip;

  return (
    <S.NavbarContainer $transparent={transparent}>
      <S.NavbarLeft>
        <S.Logo $logoSrc={logoSrc} onClick={handleLogoClick} />
        <S.Menu>
          {menuItems.map((item) => (
            <S.MenuItem
              key={item.key}
              $active={activeMenu === item.key}
              onClick={() => navigate(item.path)}
            >
              {item.label}
            </S.MenuItem>
          ))}
        </S.Menu>
      </S.NavbarLeft>
      <S.NavbarRight>
        {isLoggedIn ? (
          <>
            {/* Search Input */}
            <S.SearchInputBox>
              <img
                src={uiIcons.search}
                alt="Search"
                width={24}
                height={24}
              />
              <S.SearchInput
                placeholder=" Search Crypto"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </S.SearchInputBox>

            {/* Deposit Button */}
            <S.DepositButton onClick={() => navigate('/deposit')}>
              Deposit
            </S.DepositButton>

            {/* Assets Link */}
            <S.AssetsLink onClick={() => navigate('/assets')}>
              Assets
            </S.AssetsLink>

            {/* Profile Icon */}
            <S.IconButton
              aria-label="Profile"
              onClick={() => navigate('/account')}
              style={{ color: '#ffffff' }}
            >
              <img
                src={uiIcons.user}
                alt="User"
                width={24}
                height={24}
              />
            </S.IconButton>

            {/* Vertical Divider */}
            <S.VerticalDivider />

            {/* Chat Icon with notification badge */}
            <S.IconButtonWithBadge
              aria-label="Chat"
              style={{ color: '#ffffff' }}
            >
              <img
                src={uiIcons.message}
                alt="Message"
                width={24}
                height={24}
              />
              {hasNotification && <S.NotificationBadge />}
            </S.IconButtonWithBadge>

            {/* Icon Group */}
            <S.IconGroup>
              <S.IconButton aria-label="Download">
                <img
                  src={uiIcons.download}
                  alt="Download"
                  width={24}
                  height={24}
                />
              </S.IconButton>
              <S.IconButton
                ref={languageButtonRef}
                aria-label="Language Selector"
                onClick={handleLanguageToggle}
              >
                <img
                  src={uiIcons.language}
                  alt="Language"
                  width={24}
                  height={24}
                />
                <S.LanguageDropdown
                  ref={dropdownRef}
                  $isOpen={isLanguageDropdownOpen}
                >
                  {languages.map((lang) => (
                    <S.LanguageOption
                      key={lang.code}
                      $active={currentLang === lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                    >
                      <span>{lang.name}</span>
                      {currentLang === lang.code && (
                        <S.CheckIcon>✓</S.CheckIcon>
                      )}
                    </S.LanguageOption>
                  ))}
                </S.LanguageDropdown>
              </S.IconButton>
              <S.IconButton aria-label="Settings" onClick={handleThemeToggle}>
                <img
                  src={isDarkMode ? uiIcons.nightMode : uiIcons.settings}
                  alt="Settings"
                  width={24}
                  height={24}
                />
              </S.IconButton>
            </S.IconGroup>
          </>
        ) : (
          <>
            <S.AuthSection>
              <S.LoginLink onClick={() => navigate('/login')}>
                {t('piecrip.header.auth.login')}
              </S.LoginLink>
              <S.SignUpButton onClick={() => navigate('/signup')}>
                {t('piecrip.header.auth.signUp')}
              </S.SignUpButton>
            </S.AuthSection>
            <S.IconButton aria-label="Download">
              <img
                src={uiIcons.download}
                alt="Download"
                width={24}
                height={24}
              />
            </S.IconButton>
            <S.IconButton
              ref={languageButtonRef}
              aria-label="Language Selector"
              onClick={handleLanguageToggle}
            >
              <img
                src={uiIcons.language}
                alt="Language"
                width={24}
                height={24}
              />
              <S.LanguageDropdown
                ref={dropdownRef}
                $isOpen={isLanguageDropdownOpen}
              >
                {languages.map((lang) => (
                  <S.LanguageOption
                    key={lang.code}
                    $active={currentLang === lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                  >
                    <span>{lang.name}</span>
                    {currentLang === lang.code && <S.CheckIcon>✓</S.CheckIcon>}
                  </S.LanguageOption>
                ))}
              </S.LanguageDropdown>
            </S.IconButton>
            <S.IconButton aria-label="Settings" onClick={handleThemeToggle}>
              <img
                src={isDarkMode ? uiIcons.nightMode : uiIcons.settings}
                alt="Settings"
                width={24}
                height={24}
              />
            </S.IconButton>
          </>
        )}
      </S.NavbarRight>
    </S.NavbarContainer>
  );
};

export default PiecripHeader;
